/**
 * Tiny typed fetch client for the @thema/data-api REST service. Builds the query
 * string (dropping undefined/null params) and throws `DataApiError` on non-2xx
 * so React-Query treats 404/503/500 as errors (never silently shows bad data).
 */

export type QueryParams = Record<string, string | number | boolean | undefined | null>;

export class DataApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'DataApiError';
    this.status = status;
  }
}

function buildUrl(baseUrl: string, path: string, params?: QueryParams): string {
  const qs = new URLSearchParams();
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        qs.append(key, String(value));
      }
    }
  }
  const query = qs.toString();
  const base = baseUrl.replace(/\/$/, '');
  return `${base}${path}${query ? `?${query}` : ''}`;
}

export async function dataApiFetch<T>(
  baseUrl: string,
  path: string,
  params?: QueryParams,
  signal?: AbortSignal,
  authToken?: string | null,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  // Authenticated endpoints (the per-wallet analytics API) require the caller's
  // Google ID token as a Bearer credential; the server verifies it and enforces
  // that its bound wallet matches the requested one.
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  const res = await fetch(buildUrl(baseUrl, path, params), { signal, headers });

  if (!res.ok) {
    let message = `Data API request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      // non-JSON error body; keep the status-based message
    }
    throw new DataApiError(res.status, message);
  }

  return (await res.json()) as T;
}
